import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as TemplateIcon,
  CloudSync as CloudSyncIcon,
  ContentCopy as ContentCopyIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import CreateTemplateDialog from "./CreateTemplateDialog";
import MessageTemplateService from "../services/MessageTemplateService";
import { errorMessage } from "../shared/utils/validators";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TemplateLibraryPanel from "./TemplateLibraryPanel";
import { Tab, Tabs } from "@mui/material";

const TemplatesView = () => {
  const [listTab, setListTab] = useState("yours");
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [templates, setTemplates] = useState([]);
  const [syncingId, setSyncingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);

  // Mock data for templates
  // const templates = [
  //   {
  //     id: 1,
  //     name: "Welcome Message",
  //     category: "Marketing",
  //     status: "Approved",
  //     language: "English",
  //     lastUpdated: "2024-01-15",
  //   },
  //   {
  //     id: 2,
  //     name: "Order Confirmation",
  //     category: "Utility",
  //     status: "Pending",
  //     language: "English",
  //     lastUpdated: "2024-01-14",
  //   },
  //   {
  //     id: 3,
  //     name: "Promotional Offer",
  //     category: "Marketing",
  //     status: "Rejected",
  //     language: "Hindi",
  //     lastUpdated: "2024-01-13",
  //   },
  //   {
  //     id: 4,
  //     name: "Appointment Reminder",
  //     category: "Utility",
  //     status: "Approved",
  //     language: "English",
  //     lastUpdated: "2024-01-12",
  //   },
  //   {
  //     id: 5,
  //     name: "Festival Greetings",
  //     category: "Marketing",
  //     status: "Approved",
  //     language: "Hindi",
  //     lastUpdated: "2024-01-11",
  //   },
  // ];

  async function fetch() {
    MessageTemplateService.getAllTemplates().then((res) => setTemplates(res));
  }

  useEffect(() => {
    fetch()
  }, [])
  const handleMoreOptionsClick = (event, template) => {
    setAnchorEl(event.currentTarget);
    setActiveTemplate(template);
  };

  const handleMoreOptionsClose = () => {
    setAnchorEl(null);
    setActiveTemplate(null);
  };

  const handleEditTemplate = () => {
    console.log("Edit template:", activeTemplate);
    setCreateDialogOpen(true);
  };

  const handleDeleteTemplate = () => {
    console.log("Delete template:", activeTemplate);
    handleMoreOptionsClose();
  };

  const handleSyncToMeta = async () => {
    if (!activeTemplate?.getId()) {
      handleMoreOptionsClose();
      return;
    }
    setSyncingId(activeTemplate.getId());
    try {
      await MessageTemplateService.syncMessageTemplateToMeta(activeTemplate.getId());
      setSnackbar({
        open: true,
        message: "Template submitted to Meta. Check status in a few minutes.",
        severity: "success",
      });
      await fetch();
    } catch (error) {
      const msg = errorMessage(error);
      setSnackbar({
        open: true,
        message: msg || "Sync to Meta failed",
        severity: "error",
      });
    } finally {
      setSyncingId(null);
      handleMoreOptionsClose();
    }
  };

  const handleDuplicateTemplate = async () => {
    if (!activeTemplate?.getId()) {
      handleMoreOptionsClose();
      return;
    }
    setDuplicatingId(activeTemplate.getId());
    try {
      await MessageTemplateService.duplicateTemplate(activeTemplate.getId());
      setSnackbar({
        open: true,
        message: "Template duplicated successfully.",
        severity: "success",
      });
      await fetch();
    } catch (error) {
      const msg = errorMessage(error);
      setSnackbar({
        open: true,
        message: msg || "Failed to duplicate template",
        severity: "error",
      });
    } finally {
      setDuplicatingId(null);
      handleMoreOptionsClose();
    }
  };

  const handleCreateTemplate = () => {
    setActiveTemplate(null);
    setCreateDialogOpen(true);
  };

  async function handleSaveTemplate(templateData) {
    console.log("Saving template:", templateData);
    try {
      if (activeTemplate) {
        await MessageTemplateService.updateMessageTemplate(templateData)

       setSnackbar({
      open: true,
      message: "Template updated successfully!",
      severity: "success",
    });
      } else {
      await MessageTemplateService.createMessageTemplate(templateData)

       setSnackbar({
      open: true,
      message: "Template created successfully!",
      severity: "success",
    });
  }
    fetch();
    setCreateDialogOpen(false);
    } catch (error) {
      const message = errorMessage(error);
       if (message) {
        setSnackbar({
          open: true,
          message: `${message}`,
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Something went wrong',
          severity: "error",
        });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return { bg: "#e8f5e8", color: "#2e7d32" };
       case "APPROVED":
        return { bg: "#e8f5e8", color: "#2e7d32" };
      case "Pending":
        return { bg: "#fff3e0", color: "#f57c00" };
      case "PENDING":
        return { bg: "#fff3e0", color: "#f57c00" };
      case "FLAGGED":
      case "PAUSED":
      case "DISABLED":
        return { bg: "#fff3e0", color: "#ef6c00" };
      case "Rejected":
      case "REJECTED":
        return { bg: "#ffebee", color: "#d32f2f" };
      case "LOCAL":
        return { bg: "#e3f2fd", color: "#1565c0" };
      default:
        return { bg: "#f5f5f5", color: "#666" };
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "Marketing":
        return { bg: "#e3f2fd", color: "#1976d2" };
      case "Utility":
        return { bg: "#f3e5f5", color: "#7b1fa2" };
      case "Authentication":
      case "One-time Password":
        return { bg: "#fff3e0", color: "#e65100" };
      default:
        return { bg: "#f5f5f5", color: "#666" };
    }
  };

  const getQualityColor = (quality) => {
    const score = String(quality || "").toUpperCase();
    if (score === "GREEN") return { bg: "#e8f5e8", color: "#2e7d32" };
    if (score === "YELLOW") return { bg: "#fff8e1", color: "#f57c00" };
    if (score === "RED") return { bg: "#ffebee", color: "#d32f2f" };
    if (score === "UNKNOWN") return { bg: "#eceff1", color: "#546e7a" };
    return { bg: "#f5f5f5", color: "#666" };
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Paper elevation={0} sx={{ px: 3, pt: 2, borderBottom: "1px solid #e0e0e0", bgcolor: "#fff" }}>
        <Tabs value={listTab} onChange={(_, v) => setListTab(v)} sx={{ minHeight: 40 }}>
          <Tab value="yours" label="Your templates" sx={{ textTransform: "none", fontWeight: 600 }} />
          <Tab value="library" label="Template library" sx={{ textTransform: "none", fontWeight: 600 }} />
        </Tabs>
      </Paper>

      {listTab === "library" ? (
        <Box sx={{ p: 3, flex: 1, overflow: "auto" }}>
          <TemplateLibraryPanel onAfterClone={fetch} />
        </Box>
      ) : (
        <>
      {/* Header */}
      {templates.length > 0 && (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "#1976d2", width: 40, height: 40 }}>
              <TemplateIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "#333", fontFamily: 'Poppins, sans-serif' }}>
                Templates
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                Manage your message templates
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTemplate}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Create Template
          </Button>
        </Box>
      </Paper>
      )}
      {/* ===== EMPTY TEMPLATES PLACEHOLDER ===== */}
{templates.length === 0 && (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      textAlign: "center",
      py: 8,
      color: "#9ca3af",
    }}
  >
    <DescriptionOutlinedIcon sx={{ fontSize: 80, mb: 2 }} />

    <Typography
      sx={{
        fontSize: 20,
        fontWeight: 600,
        mb: 0.5,
        color: "#374151",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      No Templates Created Yet
    </Typography>

    <Typography
      sx={{
        fontSize: 14,
        maxWidth: 360,
        mb: 2,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      Create message templates to reuse and send messages faster
    </Typography>

    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={handleCreateTemplate}
      sx={{
        borderRadius: 2,
        textTransform: "none",
        px: 3,
      }}
    >
      Create Template
    </Button>
  </Box>
)}

      {/* Table */}
      {templates.length > 0 && (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: 0,
          "& .MuiTable-root": {
            borderCollapse: "separate",
            borderSpacing: 0,
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: "#f5f5f5",
                  // borderBottom: "2px solid #e0e0e0",
                  fontWeight: 600,
                  color: "#333",
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Template Name
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#f5f5f5",
                  // borderBottom: "2px solid #e0e0e0",
                  fontWeight: 600,
                  color: "#333",
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Category
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#f5f5f5",
                  // borderBottom: "2px solid #e0e0e0",
                  fontWeight: 600,
                  color: "#333",
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#f5f5f5",
                  fontWeight: 600,
                  color: "#333",
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Quality Score
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#f5f5f5",
                  // borderBottom: "2px solid #e0e0e0",
                  fontWeight: 600,
                  color: "#333",
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Language
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: "#f5f5f5",
                  // borderBottom: "2px solid #e0e0e0",
                  fontWeight: 600,
                  fontFamily: 'Poppins, sans-serif',
                  color: "#333",
                }}
              >
                Last Updated
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  backgroundColor: "#f5f5f5",
                  // borderBottom: "2px solid #e0e0e0",
                  fontWeight: 600,
                  color: "#333",
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow
                key={template.getId()}
                sx={{
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  },
                  "&:last-child td": {
                    borderBottom: 0,
                  },
                }}
              >
                <TableCell>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: "#1976d2",
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {template.getTemplateName()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={template.getCategory()}
                    size="small"
                    sx={{
                      backgroundColor: getCategoryColor(template.getCategory()).bg,
                      color: getCategoryColor(template.getCategory()).color,
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Tooltip title={template.getTemplateStatusReason() || ""} arrow disableHoverListener={!template.getTemplateStatusReason()}>
                      <Chip
                        label={template.getStatus()}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(template.getStatus()).bg,
                          color: getStatusColor(template.getStatus()).color,
                          fontWeight: 500,
                          cursor: template.getTemplateStatusReason() ? "help" : "default",
                          border: template.getTemplateStatusReason() ? "1px dashed rgba(0,0,0,0.25)" : "none",
                        }}
                      />
                    </Tooltip>
                    {template.getTemplateStatusReason() ? (
                      <Tooltip title={template.getTemplateStatusReason()} arrow>
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "#f57c00", cursor: "help" }}
                        />
                      </Tooltip>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell>
                  {template.getTemplateQualityScore() ? (
                    <Chip
                      label={String(template.getTemplateQualityScore()).toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: getQualityColor(template.getTemplateQualityScore()).bg,
                        color: getQualityColor(template.getTemplateQualityScore()).color,
                        fontWeight: 500,
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{template.getLanguage()}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(template.getUpdatedAt()).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(event) => handleMoreOptionsClick(event, template)}
                    sx={{ color: "#666" }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      {/* More Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMoreOptionsClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            minWidth: 150,
          },
        }}
      >
        <MenuItem onClick={handleEditTemplate} sx={{ gap: 1 }}>
          <EditIcon fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem
          onClick={handleDuplicateTemplate}
          disabled={duplicatingId === activeTemplate?.getId()}
          sx={{ gap: 1 }}
        >
          <ContentCopyIcon fontSize="small" />
          {duplicatingId === activeTemplate?.getId() ? "Duplicating…" : "Duplicate"}
        </MenuItem>
        {activeTemplate && activeTemplate.getLocalOnly() && (
          <MenuItem
            onClick={handleSyncToMeta}
            disabled={syncingId === activeTemplate?.getId()}
            sx={{ gap: 1 }}
          >
            <CloudSyncIcon fontSize="small" />
            {syncingId === activeTemplate?.getId() ? "Syncing…" : "Sync to Meta"}
          </MenuItem>
        )}
        {/* <MenuItem
          onClick={handleDeleteTemplate}
          sx={{ gap: 1, color: "#d32f2f" }}
        >
          <DeleteIcon fontSize="small" />
          Delete
        </MenuItem> */}
      </Menu>

      {/* Create Template Dialog */}
      <CreateTemplateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleSaveTemplate}
        initialValues={activeTemplate}
        isEdit={!!activeTemplate}
      />
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplatesView;
